import { computed, signal, type Provider, type Signal } from '@angular/core';
import {
  MESSENGER_AUTH,
  MESSENGER_CONFIG,
  MESSENGER_DESKTOP_NOTIFIER,
  MESSENGER_NOTIFICATION_REPOSITORY,
  MESSENGER_REALTIME,
  MESSENGER_REPOSITORY,
  messengerLocalConfig,
  messengerFailure,
  messengerOk,
  type CalendarReadiness,
  type Conversation,
  type ConversationId,
  type ConversationNote,
  type ConversationQuery,
  type ConversationThread,
  type FollowUp,
  type FollowUpChanges,
  type FollowUpDraft,
  type MediaAsset,
  type Message,
  type MessagingAvailability,
  type MessagingAvailabilityValue,
  type MessengerAccount,
  type MessengerAuth,
  type MessengerAuthStatus,
  type MessengerNotification,
  type MessengerNotificationRepository,
  type MessengerRealtime,
  type MessengerRealtimeStatus,
  type MessengerRepository,
  type MessengerResult,
  type MessengerRole,
  type MessengerRuntimeFeatures,
  type NoteDraft,
  type Recommendation,
  type SendMessageCommand,
  type SuggestionFeedbackRequest,
  type SuggestionFeedbackResponse,
} from '@crm/messenger';
import {
  DEFAULT_ACTOR,
  ExperienceContextService,
} from '../core/experience-context';
import {
  e2eConversations,
  e2eFollowUps,
  e2eMedia,
  e2eNotes,
  e2eNotifications,
  e2eRecommendations,
  e2eRuntimeFeatures,
  e2eThread,
} from './messenger-e2e.fixtures';
import { WebNotificationAdapter } from './web-notification.adapter';

function messengerAccountFromActor(
  actor = DEFAULT_ACTOR,
): MessengerAccount {
  const role = actor.role as MessengerRole;
  return {
    name: actor.name,
    email: actor.email,
    tenantId: actor.teamId ?? 'mello-mock',
    roles: [role],
  };
}

export class MockMessengerAuth implements MessengerAuth {
  private readonly ctx: ExperienceContextService | undefined;

  constructor(ctx?: ExperienceContextService) {
    this.ctx = ctx;
  }

  readonly status: Signal<MessengerAuthStatus> = signal('authenticated');
  private readonly _account = computed(() =>
    messengerAccountFromActor(this.ctx?.actor() ?? DEFAULT_ACTOR),
  );
  readonly account: Signal<MessengerAccount | null> =
    this._account as unknown as Signal<MessengerAccount | null>;
  private readonly _roles = computed(() => this._account().roles);
  readonly roles: Signal<readonly MessengerRole[]> = this._roles;
  readonly error: Signal<string> = signal('');

  initialize(): Promise<void> {
    return Promise.resolve();
  }

  accessToken(): Promise<string> {
    return Promise.resolve('mock-access-token');
  }

  signIn(): Promise<void> {
    return Promise.resolve();
  }

  signOut(): Promise<void> {
    return Promise.resolve();
  }

  consumeReturnUrl(): string | null {
    return null;
  }
}

export class MockMessengerRepository implements MessengerRepository {
  private conversations = e2eConversations();
  private followUps = new Map<string, FollowUp[]>();
  private notes = new Map<string, ConversationNote[]>();
  private media = e2eMedia();
  private recommendations = e2eRecommendations();
  private sent = new Map<string, Message[]>();
  private sentCounter = 0;
  private availabilityState: MessagingAvailability = {
    value: 'available',
    version: 1,
    manualOverrideActive: false,
  };

  listConversations(
    query: ConversationQuery,
  ): Promise<MessengerResult<readonly Conversation[]>> {
    const search = (query.search ?? '').trim().toLowerCase();
    const items = search
      ? this.conversations.filter((item) =>
          [item.customerName, item.preview, item.customerPhone]
            .join(' ')
            .toLowerCase()
            .includes(search),
        )
      : this.conversations;
    return Promise.resolve(messengerOk(items));
  }

  getConversation(id: ConversationId): Promise<MessengerResult<ConversationThread>> {
    const thread = e2eThread(id);
    if (!thread)
      return Promise.resolve(
        messengerFailure('not-found', 'Conversation not found.', { status: 404 }),
      );
    return Promise.resolve(messengerOk(this.withSentMessages(thread)));
  }

  sendMessage(
    id: ConversationId,
    command: SendMessageCommand,
  ): Promise<MessengerResult<Message>> {
    const thread = e2eThread(id);
    if (!thread)
      return Promise.resolve(
        messengerFailure('not-found', 'Conversation not found.', { status: 404 }),
      );
    this.sentCounter += 1;
    const sentAt = new Date().toISOString();
    const outgoing: Message = {
      id: `m-sent-${this.sentCounter}`,
      clientMessageId: command.clientMessageId,
      direction: 'outbound',
      senderPersona: 'loan-officer',
      senderLabel: 'Loan officer',
      who: 'You',
      initials: 'YO',
      channel: thread.conversation.channel,
      sentAt,
      timeLabel: new Date(sentAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
      status: 'Sent',
      text: command.content,
      attachments: [],
      acknowledged: true,
      failed: false,
      isLastRead: true,
    };
    this.sent.set(id, [...(this.sent.get(id) ?? []), outgoing]);
    return Promise.resolve(messengerOk(outgoing));
  }

  private withSentMessages(thread: ConversationThread): ConversationThread {
    const extra = this.sent.get(thread.conversation.id) ?? [];
    if (!extra.length) return thread;
    const messages = [
      ...thread.messages.map((m) => ({ ...m, isLastRead: false })),
      ...extra,
    ];
    return { ...thread, messages };
  }

  markRead(id: ConversationId): Promise<MessengerResult<Conversation>> {
    return this.mutateConversation(id, { unreadCount: 0 });
  }

  markUnread(id: ConversationId): Promise<MessengerResult<Conversation>> {
    return this.mutateConversation(id, { unreadCount: 1 });
  }

  setPinned(id: ConversationId, pinned: boolean): Promise<MessengerResult<Conversation>> {
    return this.mutateConversation(id, { pinned });
  }

  setFlagged(id: ConversationId, flagged: boolean): Promise<MessengerResult<Conversation>> {
    return this.mutateConversation(id, { flagged });
  }

  archive(id: ConversationId): Promise<MessengerResult<Conversation>> {
    return this.mutateConversation(id, { archived: true, status: 'closed' });
  }

  private mutateConversation(
    id: ConversationId,
    changes: Partial<Conversation>,
  ): Promise<MessengerResult<Conversation>> {
    const index = this.conversations.findIndex((item) => item.id === id);
    if (index < 0)
      return Promise.resolve(
        messengerFailure('not-found', 'Conversation not found.', { status: 404 }),
      );
    const updated = { ...this.conversations[index], ...changes };
    this.conversations = [
      ...this.conversations.slice(0, index),
      updated,
      ...this.conversations.slice(index + 1),
    ];
    return Promise.resolve(messengerOk(updated));
  }

  getRuntimeFeatures(): Promise<MessengerResult<MessengerRuntimeFeatures>> {
    return Promise.resolve(messengerOk(e2eRuntimeFeatures()));
  }

  getRecommendations(): Promise<MessengerResult<readonly Recommendation[]>> {
    return Promise.resolve(messengerOk(this.recommendations));
  }

  regenerateRecommendations(): Promise<MessengerResult<readonly Recommendation[]>> {
    this.recommendations = this.recommendations.map((item, index) => ({
      ...item,
      id: `${item.id}-r`,
      suggestionBatchVersion: 2,
      content: `Regenerated suggestion ${index + 1}`,
    }));
    return Promise.resolve(messengerOk(this.recommendations));
  }

  submitSuggestionFeedback(
    _id: ConversationId,
    feedback: SuggestionFeedbackRequest,
  ): Promise<MessengerResult<SuggestionFeedbackResponse>> {
    if (feedback.rating === 'down' && !feedback.comment?.trim()) {
      return Promise.resolve(
        messengerFailure('validation', 'A reason is required for negative feedback.'),
      );
    }
    return Promise.resolve(
      messengerOk({
        accepted: true,
        correlationId: `mock-suggestion-feedback-${feedback.suggestionId}`,
        feedbackId: `suggestion-feedback|mock-${feedback.suggestionId}`,
      }),
    );
  }

  listMedia(): Promise<MessengerResult<readonly MediaAsset[]>> {
    return Promise.resolve(messengerOk(this.media));
  }

  listFollowUps(id: ConversationId): Promise<MessengerResult<readonly FollowUp[]>> {
    if (!this.followUps.has(id)) this.followUps.set(id, e2eFollowUps(id));
    return Promise.resolve(messengerOk(this.followUps.get(id) ?? []));
  }

  createFollowUp(
    id: ConversationId,
    draft: FollowUpDraft,
  ): Promise<MessengerResult<FollowUp>> {
    const items = [...(this.followUps.get(id) ?? e2eFollowUps(id))];
    const startsAt = draft.startsAt;
    const endsAt = new Date(
      new Date(startsAt).getTime() + (draft.durationMinutes ?? 30) * 60000,
    ).toISOString();
    const created: FollowUp = {
      id: `fu-new-${items.length + 1}`,
      type: draft.type,
      startsAt,
      endsAt,
      status: 'scheduled',
      details: draft.details ?? '',
      channel: 'sms',
    };
    this.followUps.set(id, [...items, created]);
    return Promise.resolve(messengerOk(created));
  }

  updateFollowUp(
    id: ConversationId,
    followUpId: string,
    changes: FollowUpChanges,
  ): Promise<MessengerResult<FollowUp>> {
    const items = this.followUps.get(id) ?? e2eFollowUps(id);
    const index = items.findIndex((item) => item.id === followUpId);
    if (index < 0)
      return Promise.resolve(
        messengerFailure('not-found', 'Follow-up not found.', { status: 404 }),
      );
    const current = items[index];
    const updated: FollowUp = {
      ...current,
      type: changes.type ?? current.type,
      status: changes.status ?? current.status,
      startsAt: changes.startsAt ?? current.startsAt,
      endsAt: changes.endsAt ?? current.endsAt,
      details: changes.note ?? changes.agenda ?? current.details,
    };
    const next = [...items];
    next[index] = updated;
    this.followUps.set(id, next);
    return Promise.resolve(messengerOk(updated));
  }

  listNotes(id: ConversationId): Promise<MessengerResult<readonly ConversationNote[]>> {
    if (!this.notes.has(id)) this.notes.set(id, e2eNotes(id));
    return Promise.resolve(messengerOk(this.notes.get(id) ?? []));
  }

  createNote(
    id: ConversationId,
    draft: NoteDraft,
  ): Promise<MessengerResult<ConversationNote>> {
    const items = this.notes.get(id) ?? e2eNotes(id);
    const created: ConversationNote = {
      id: `note-new-${items.length + 1}`,
      body: draft.body,
      createdAt: new Date().toISOString(),
      archived: false,
      author: 'Will Dugan',
      mine: true,
    };
    this.notes.set(id, [created, ...items]);
    return Promise.resolve(messengerOk(created));
  }

  setNoteArchived(
    id: ConversationId,
    noteId: string,
    archived: boolean,
  ): Promise<MessengerResult<ConversationNote>> {
    const items = this.notes.get(id) ?? e2eNotes(id);
    const index = items.findIndex((item) => item.id === noteId);
    if (index < 0)
      return Promise.resolve(
        messengerFailure('not-found', 'Note not found.', { status: 404 }),
      );
    const updated = { ...items[index], archived };
    const next = [...items];
    next[index] = updated;
    this.notes.set(id, next);
    return Promise.resolve(messengerOk(updated));
  }

  generateNote(id: ConversationId): Promise<MessengerResult<ConversationNote>> {
    const items = this.notes.get(id) ?? e2eNotes(id);
    const created: ConversationNote = {
      id: `note-ai-${items.length + 1}`,
      body: 'Generated summary: borrower confirmed October statements will be sent today.',
      createdAt: new Date().toISOString(),
      archived: false,
      author: 'Will Dugan',
      generated: true,
      mine: true,
    };
    this.notes.set(id, [created, ...items]);
    return Promise.resolve(messengerOk(created));
  }

  getCalendarReadiness(): Promise<MessengerResult<CalendarReadiness>> {
    return Promise.resolve(
      messengerOk({
        status: 'configured',
        configured: true,
        readyForLive: false,
        canMutateLive: false,
        mutationAllowed: true,
        missingConfigKeys: [],
        configuredConfigKeys: ['mock-calendar'],
        reasonCodes: ['mock-calendar-readiness-only'],
      }),
    );
  }

  getMessagingAvailability(): Promise<MessengerResult<MessagingAvailability>> {
    return Promise.resolve(messengerOk(this.availabilityState));
  }

  setMessagingAvailability(
    value: MessagingAvailabilityValue,
    expectedVersion: number,
  ): Promise<MessengerResult<MessagingAvailability>> {
    this.availabilityState = {
      value,
      version: Math.max(this.availabilityState.version, expectedVersion) + 1,
      manualOverrideActive: value === 'away',
      effectiveSource: 'manual-override',
    };
    return Promise.resolve(messengerOk(this.availabilityState));
  }
}

export class MockNotificationRepository implements MessengerNotificationRepository {
  private items: MessengerNotification[] = e2eNotifications();

  list(): Promise<MessengerResult<readonly MessengerNotification[]>> {
    return Promise.resolve(messengerOk(this.items.filter((i) => i.status !== 'dismissed')));
  }

  markRead(id: string): Promise<MessengerResult<MessengerNotification>> {
    return this.patch(id, { status: 'read', readAt: new Date().toISOString() });
  }

  markUnread(id: string): Promise<MessengerResult<MessengerNotification>> {
    return this.patch(id, { status: 'unread', readAt: '' });
  }

  markAllRead(): Promise<MessengerResult<readonly MessengerNotification[]>> {
    const now = new Date().toISOString();
    this.items = this.items.map((item) =>
      item.status === 'unread' ? { ...item, status: 'read', readAt: now } : item,
    );
    return Promise.resolve(messengerOk(this.items));
  }

  dismiss(id: string): Promise<MessengerResult<MessengerNotification>> {
    return this.patch(id, { status: 'dismissed' });
  }

  private patch(
    id: string,
    changes: Partial<MessengerNotification>,
  ): Promise<MessengerResult<MessengerNotification>> {
    const index = this.items.findIndex((item) => item.id === id);
    if (index < 0)
      return Promise.resolve(
        messengerFailure('not-found', 'Notification not found.', { status: 404 }),
      );
    const updated = { ...this.items[index], ...changes };
    const next = [...this.items];
    next[index] = updated;
    this.items = next;
    return Promise.resolve(messengerOk(updated));
  }
}

export class MockMessengerRealtime implements MessengerRealtime {
  readonly status: Signal<MessengerRealtimeStatus> = signal('idle');

  connect(): Promise<void> {
    return Promise.resolve();
  }

  disconnect(): Promise<void> {
    return Promise.resolve();
  }
}

export function provideMelloMessengerMock(): Provider[] {
  return [
    { provide: MESSENGER_CONFIG, useFactory: () => messengerLocalConfig() },
    { provide: MESSENGER_AUTH, useFactory: () => new MockMessengerAuth() },
    { provide: MESSENGER_REPOSITORY, useFactory: () => new MockMessengerRepository() },
    {
      provide: MESSENGER_NOTIFICATION_REPOSITORY,
      useFactory: () => new MockNotificationRepository(),
    },
    { provide: MESSENGER_REALTIME, useFactory: () => new MockMessengerRealtime() },
    { provide: MESSENGER_DESKTOP_NOTIFIER, useFactory: () => new WebNotificationAdapter() },
  ];
}
